import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setStatus('error');
      setMessage('This confirmation link is missing its token.');
      return;
    }
    authAPI
      .confirmEmail(token)
      .then(() => {
        setStatus('success');
        setMessage('Your email address has been confirmed. Thank you!');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'We could not confirm your email. The link may have expired.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="confirm-email-page">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#6B8E6B]" />
              <h1 className="text-xl font-bold">Confirming your email…</h1>
              <p className="text-muted-foreground text-sm">Just a moment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#6B8E6B]/15 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[#6B8E6B]" />
              </div>
              <h1 className="text-xl font-bold" data-testid="confirm-success">Email confirmed 🎉</h1>
              <p className="text-muted-foreground text-sm">{message}</p>
              <Button className="w-full bg-[#C85A42] hover:bg-[#B04A35]" onClick={() => navigate('/login')} data-testid="confirm-go-login">
                Go to login
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-xl font-bold" data-testid="confirm-error">Confirmation failed</h1>
              <p className="text-muted-foreground text-sm">{message}</p>
              <p className="text-muted-foreground text-xs flex items-center justify-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Log in and use the "Resend" button on the confirmation banner to get a fresh link.
              </p>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/login">Go to login</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
